/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React from "react"
import { useTheme } from "@cd2/theme"
import { brandColors } from "../EOSThemeProvider"
import { H4 } from "@cd2/text"

interface IProps {
  className?: string
  children?: React.ReactNode
  color?: string
  header?: React.ReactNode
  footer?: React.ReactNode
  includeSeparator?: boolean
  headerRightNode?: React.ReactNode

  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined
}

FloatingBox.vars = {
  childPadding: "--floating-box-child-padding",
  borderColor: "--floating-box-border-color",
  borderRadius: "--floating-box-border-radius",
  footerBackgroundColor: "--floating-box-footer-background-color",
  childBackgroundColor: "--floating-box-child-background-color",
  footerPadding: "--floating-box-footer-padding",
  headerPadding: "--floating-box-header-padding",
  backgroundColor: "--floating-box-footer-background-color",
}

export function FloatingBox({
  className,
  children,
  color,
  footer,
  header,
  onClick,
  headerRightNode,
  includeSeparator = true,
}: IProps) {
  const theme = useTheme()
  return (
    <div
      onClick={onClick}
      css={css`
        --floating-box-child-padding: 16px;
        --floating-box-header-padding: 16px;
        --floating-box-footer-padding: 16px;
        --floating-box-border-radius: ${theme.borderRadius}px;
        --floating-box-border-color: ${brandColors.lighterNight};
        --floating-box-footer-background-color: ${color ? color : "white"};
        --floating-box-child-background-color: ${color ? color : "white"};

        border: 1px solid var(--floating-box-border-color);
        border-radius: var(--floating-box-border-radius);
        background: ${color ? color : "white"};
        display: flex;
        flex-direction: column;

        ${onClick ? "cursor: pointer;" : ""}
      `}
      className={className}
    >
      {(header || headerRightNode) && (
        <div
          css={css`
            padding: var(--floating-box-header-padding);
            display: ${headerRightNode ? "flex" : "block"};
            align-items: center;
            justify-content: space-between;
            ${includeSeparator ? `border-bottom: 1px solid var(--floating-box-border-color);` : ""}
          `}
        >
          {typeof header === "string" ? <H4>{header}</H4> : header}
          {headerRightNode && (
            <div
              css={css`
                margin: calc(0px - var(--floating-box-header-padding)) 0;
              `}
            >
              {headerRightNode}
            </div>
          )}
        </div>
      )}
      {children && (
        <div
          css={css`
            padding: var(--floating-box-child-padding);
            background: var(--floating-box-child-background-color);
            flex: 1;
            border-top-left-radius: ${header ? 0 : "var(--floating-box-border-radius)"};
            border-top-right-radius: ${header ? 0 : "var(--floating-box-border-radius)"};
            border-bottom-left-radius: ${footer ? 0 : "var(--floating-box-border-radius)"};
            border-bottom-right-radius: ${footer ? 0 : "var(--floating-box-border-radius)"};
          `}
        >
          {children}
        </div>
      )}
      {footer && (
        <div
          css={css`
            padding: var(--floating-box-footer-padding);
            border-top: 1px solid var(--floating-box-border-color);
            background-color: var(--floating-box-footer-background-color);
            border-bottom-left-radius: var(--floating-box-border-radius);
            border-bottom-right-radius: var(--floating-box-border-radius);
          `}
        >
          {footer}
        </div>
      )}
    </div>
  )
}

function Separator() {
  return (
    <div
      css={css`
        height: 1px;
        width: 100% + 32px;
        margin: 0 calc(-1 * var(--floating-box-child-padding));
        background: var(--floating-box-border-color);
      `}
    />
  )
}

function VerticalSeparator() {
  return (
    <div
      css={css`
        height: 100% + 32px;
        width: 1px;
        margin: calc(-1 * var(--floating-box-child-padding)) 0;
        background: var(--floating-box-border-color);
      `}
    />
  )
}

FloatingBox.Separator = Separator
FloatingBox.VerticalSeparator = VerticalSeparator
